import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import Testimonials from '../components/Testimonials';
import './About.css';

const About = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('de');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setLanguage(user.language || 'de');
      } catch (e) {
        setLanguage(navigator.language?.startsWith('de') ? 'de' : 'en');
      }
    } else {
      setLanguage(navigator.language?.startsWith('de') ? 'de' : 'en');
    }
  }, []);

  const content = {
    de: {
      hero: {
        title: 'Über StoryTales',
        subtitle: 'Magische personalisierte Geschichten für Kinder, erstellt mit modernster KI-Technologie'
      },
      mission: {
        title: 'Unsere Mission',
        text: 'Wir glauben, dass jedes Kind ein einzigartiges Abenteuer verdient. Mit StoryTales möchten wir die Fantasie von Kindern anregen, indem wir personalisierte Geschichten schaffen, in denen sie die Helden ihrer eigenen magischen Welten sind.'
      },
      howItWorks: {
        title: 'Wie funktioniert es?',
        steps: [
          {
            icon: '✏️',
            title: '1. Eingeben',
            description: 'Geben Sie den Namen, das Alter Ihres Kindes und ein Thema ein. Wählen Sie aus unseren Vorlagen oder erstellen Sie Ihr eigenes Thema.'
          },
          {
            icon: '🤖',
            title: '2. KI generiert',
            description: 'Unsere fortschrittliche KI (Google Gemini 2.5 Pro) erstellt eine altersgerechte, einzigartige Geschichte mit Ihrem Kind als Hauptfigur.'
          },
          {
            icon: '🎨',
            title: '3. Illustrationen',
            description: 'OpenAI DALL-E 3 oder Google Nano Banana generieren wunderschöne, passende Illustrationen für jede Seite Ihrer Geschichte.'
          },
          {
            icon: '📚',
            title: '4. Genießen',
            description: 'Lesen Sie die Geschichte online, exportieren Sie sie als PDF, drucken Sie sie aus oder senden Sie sie an Ihr Kindle!'
          }
        ]
      },
      features: {
        title: 'Was macht uns besonders?',
        items: [
          {
            icon: '🧠',
            title: 'Modernste KI',
            description: 'Powered by Google Gemini 2.5 Pro, OpenAI DALL-E 3 und Google Nano Banana – die besten KI-Modelle für Text und Bilder.'
          },
          {
            icon: '👶',
            title: 'Altersgerecht',
            description: 'Textlänge und Komplexität werden automatisch an das Alter Ihres Kindes angepasst.'
          },
          {
            icon: '🌍',
            title: 'Mehrsprachig',
            description: 'Erhältlich in Deutsch und Englisch – perfekt für mehrsprachige Familien.'
          },
          {
            icon: '📸',
            title: 'Foto-Upload (Pro)',
            description: 'Laden Sie ein Foto Ihres Kindes hoch, und die KI erstellt einen noch personalisierteren Charakter.'
          },
          {
            icon: '📧',
            title: 'Kindle-Integration',
            description: 'Senden Sie Geschichten direkt an Ihr Kindle-Gerät (Pro-Feature).'
          },
          {
            icon: '🔒',
            title: 'Datenschutz',
            description: 'DSGVO-konform, keine Datenweitergabe, volle Kontrolle über Ihre Daten.'
          }
        ]
      },
      technology: {
        title: 'Unsere Technologie',
        description: 'StoryTales nutzt die neuesten Durchbrüche in der künstlichen Intelligenz:',
        items: [
          '<strong>Google Gemini 2.5 Pro:</strong> Das fortschrittlichste Sprachmodell für kreative, altersgerechte Geschichtenentwicklung.',
          '<strong>OpenAI DALL-E 3 & Google Nano Banana:</strong> State-of-the-art Bildgenerierung für hochwertige, themenbezogene Illustrationen.',
          '<strong>Cloudinary:</strong> Schnelle, optimierte Bildbereitstellung für beste Performance.',
          '<strong>Character Consistency:</strong> Spezieller KI-Agent sorgt für konsistente Charakterdarstellung durch die gesamte Geschichte.'
        ]
      },
      team: {
        title: 'Unser Team',
        description: 'StoryTales wurde von einem Team aus KI-Entwicklern, Pädagogen und Eltern entwickelt, die eine Leidenschaft für Storytelling und Technologie teilen. Unser Ziel: Kindern weltweit magische, personalisierte Leseerlebnisse zu bieten.'
      },
      cta: {
        title: 'Bereit, Ihre erste Geschichte zu erstellen?',
        button: 'Jetzt loslegen'
      }
    },
    en: {
      hero: {
        title: 'About StoryTales',
        subtitle: 'Magical personalized stories for children, created with cutting-edge AI technology'
      },
      mission: {
        title: 'Our Mission',
        text: 'We believe every child deserves a unique adventure. With StoryTales, we aim to spark children\'s imagination by creating personalized stories where they become heroes of their own magical worlds.'
      },
      howItWorks: {
        title: 'How It Works',
        steps: [
          {
            icon: '✏️',
            title: '1. Input',
            description: 'Enter your child\'s name, age, and a theme. Choose from our templates or create your own.'
          },
          {
            icon: '🤖',
            title: '2. AI Generates',
            description: 'Our advanced AI (Google Gemini 2.5 Pro) creates an age-appropriate, unique story with your child as the main character.'
          },
          {
            icon: '🎨',
            title: '3. Illustrations',
            description: 'OpenAI DALL-E 3 or Google Nano Banana generate beautiful, matching illustrations for each page of your story.'
          },
          {
            icon: '📚',
            title: '4. Enjoy',
            description: 'Read online, export as PDF, print it out, or send it to your Kindle!'
          }
        ]
      },
      features: {
        title: 'What Makes Us Special?',
        items: [
          {
            icon: '🧠',
            title: 'Cutting-Edge AI',
            description: 'Powered by Google Gemini 2.5 Pro, OpenAI DALL-E 3 and Google Nano Banana – the best AI models for text and images.'
          },
          {
            icon: '👶',
            title: 'Age-Appropriate',
            description: 'Text length and complexity automatically adjusted to your child\'s age.'
          },
          {
            icon: '🌍',
            title: 'Multilingual',
            description: 'Available in German and English – perfect for multilingual families.'
          },
          {
            icon: '📸',
            title: 'Photo Upload (Pro)',
            description: 'Upload a photo of your child, and the AI creates an even more personalized character.'
          },
          {
            icon: '📧',
            title: 'Kindle Integration',
            description: 'Send stories directly to your Kindle device (Pro feature).'
          },
          {
            icon: '🔒',
            title: 'Privacy',
            description: 'GDPR compliant, no data sharing, full control over your data.'
          }
        ]
      },
      technology: {
        title: 'Our Technology',
        description: 'StoryTales leverages the latest breakthroughs in artificial intelligence:',
        items: [
          '<strong>Google Gemini 2.5 Pro:</strong> The most advanced language model for creative, age-appropriate storytelling.',
          '<strong>OpenAI DALL-E 3 & Google Nano Banana:</strong> State-of-the-art image generation for high-quality, thematic illustrations.',
          '<strong>Cloudinary:</strong> Fast, optimized image delivery for best performance.',
          '<strong>Character Consistency:</strong> Special AI agent ensures consistent character portrayal throughout the story.'
        ]
      },
      team: {
        title: 'Our Team',
        description: 'StoryTales was developed by a team of AI developers, educators, and parents who share a passion for storytelling and technology. Our goal: to provide children worldwide with magical, personalized reading experiences.'
      },
      cta: {
        title: 'Ready to Create Your First Story?',
        button: 'Get Started'
      }
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="about-page">
      <div className="about-header">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="back-button"
        >
          ← {language === 'de' ? 'Zurück' : 'Back'}
        </Button>
      </div>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1>{t.hero.title}</h1>
          <p>{t.hero.subtitle}</p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="cta-button hero-cta"
          >
            {t.cta.button} →
          </Button>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-section">
        <div className="about-container">
          <h2>{t.mission.title}</h2>
          <p className="about-mission-text">{t.mission.text}</p>
        </div>
      </section>

      {/* How It Works */}
      <section className="about-section alt-bg">
        <div className="about-container">
          <h2>{t.howItWorks.title}</h2>
          <div className="how-it-works-grid">
            {t.howItWorks.steps.map((step, index) => (
              <div key={index} className="how-it-works-card">
                <div className="how-it-works-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="about-section">
        <div className="about-container">
          <h2>{t.features.title}</h2>
          <div className="features-grid">
            {t.features.items.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="about-section alt-bg">
        <div className="about-container">
          <h2>{t.technology.title}</h2>
          <p className="technology-intro">{t.technology.description}</p>
          <ul className="technology-list">
            {t.technology.items.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>
      </section>

      {/* Team */}
      <section className="about-section">
        <div className="about-container">
          <h2>{t.team.title}</h2>
          <p className="team-description">{t.team.description}</p>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials language={language} />

      {/* CTA */}
      <section className="about-cta">
        <div className="about-container">
          <h2>{t.cta.title}</h2>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="cta-button"
          >
            {t.cta.button} →
          </Button>
        </div>
      </section>

      <Footer language={language} />
    </div>
  );
};

export default About;
